import { useQueryClient } from "@tanstack/react-query";
import { AnalyticsComponentProvider, AddNewButton, ConnectionItem, Flex, Heading } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { usePostHog } from "posthog-js/react";
import { filteredAndSortedDataSourcesNext, useSpaNavigate } from "shared-ui";
import { useMount } from "shared-utils";
import { useCustomerAppConfig } from "undercarriage";

import { COMMON_MESSAGES } from "../../components/common-messages";
import { ACCOUNTING_CONNECTOR_SLUGS, NAVIGATION_PATHS } from "../../components/constants";
import { useAccountingDocumentsValidation } from "../../hooks/useAccountingDocumentsValidation";
import { useBankingSources } from "../../queries/bank-connect/queries";
import { usePlatformConnectors } from "../../queries/connectors/queries";
import { useDocumentQuery } from "../../queries/document-uploads/queries";
import { useValidatedDocumentQuery } from "../../queries/document-uploads/validated-documents-queries";
import { useFundingApplication, useFundingApplicationMutation } from "../../queries/funding-application/queries";
import { UploadDocumentType } from "../../types";
import { isGoogleAnalyticsRequired } from "../../utils/funding-journey";
import { getIsFundingApplicationSubmittableQueryOptions } from "../../queries/funding-application-configuration/queries";
import { getPlatformsForApplication } from "../../hooks/navigation-next/use-funding-application-navigation-next";

import { usePlatformConnectionsAsConnectionItems } from "./hooks";
import {
  unsupportedPlatformsAsConnectionItemsProps,
  useBankConnectionsAsConnectionItems,
  useValidatedDocumentsAsConnectionItems,
  useAdditionalDocumentsAsConnectionItems,
  useCreditCheckConsentAsConnectionItem,
} from "./connection-item-props";
import { canPlatformBeRemovedNext } from "./utils/platform-removal-validator";
import {
  AccountingRemovalModal,
  BankConnectionRemovalModal,
  CannotRemovePlatformModal,
  ConfirmPlatformRemovalModal,
  ManagementAccountsRemovalModal,
  NotReadyModal,
  OptionalDocumentRemovalModal,
  RemovalModals,
  RequiredDocumentRemovalModal,
} from "./components";
import { MESSAGES } from "./message-definitions";

export interface ApplicationSummaryNextProps {
  fundingApplicationStages: FundingApplicationSchemas.CustomerFundingApplicationStagesScreen;
  isNewOnboardingEnabled: boolean;
}
export const ApplicationSummaryNext = ({
  fundingApplicationStages,
  isNewOnboardingEnabled,
}: ApplicationSummaryNextProps) => {
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const intl = useIntl();
  const posthog = usePostHog();
  const queryClient = useQueryClient();

  useMount(() => {
    posthog?.capture("onboarding_summary_viewed");
  });

  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const submittableQueryOptions = getIsFundingApplicationSubmittableQueryOptions(customerDomain, fundingApplication.id);
  const { data: connectors } = usePlatformConnectors(customerDomain);
  const { data: bankingSources } = useBankingSources(customerDomain);

  const connectorSlugsForJourney = getPlatformsForApplication(fundingApplication, fundingApplicationStages);

  const showGAInNoAction =
    fundingApplication?.selectedConnectorSlugs?.includes("ga") &&
    !isGoogleAnalyticsRequired(fundingApplication.selectedConnectorSlugs);

  const orderedConnectors = connectorSlugsForJourney
    .map((slug) => {
      const connector = connectors.find((c) => c.slug === slug);
      if (!connector) {
        return null;
      }

      // Skip GA if it should be shown in "no action" section
      if (slug === "ga" && showGAInNoAction) {
        return null;
      }

      return {
        ...connector,
      };
    })
    .filter((c): c is NonNullable<typeof c> => c !== null);

  const {
    shoppingDataSources: shoppingConnectors,
    marketingDataSources: marketingConnectors,
    accountingDataSources: accountingConnectors,
  } = filteredAndSortedDataSourcesNext(orderedConnectors);

  const [openModal, setOpenModal] = useState<RemovalModals | undefined>();
  const [selectedSlug, setSelectedSlug] = useState<string | undefined>();
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);

  const removeBasicPlatform = useCallback(async () => {
    const updatedSlugs = fundingApplication.selectedConnectorSlugs.filter((platform) => platform !== selectedSlug);
    await fundingApplicationMutation.mutateAsync({ id: fundingApplication.id, selectedConnectorSlugs: updatedSlugs });
    await queryClient.invalidateQueries({
      queryKey: submittableQueryOptions.queryKey,
    });
    setSelectedSlug(undefined);
  }, [
    fundingApplication.id,
    fundingApplication.selectedConnectorSlugs,
    fundingApplicationMutation,
    queryClient,
    selectedSlug,
    submittableQueryOptions.queryKey,
  ]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  const removeAccountingPlatform = useCallback(async () => {
    const updatedSlugs = fundingApplication.selectedConnectorSlugs.filter((platform) => platform !== selectedSlug);
    const updatedUnsupportedAccounting =
      selectedSlug === "codat_accounting" ? [] : fundingApplication.selectedUnsupportedAccounting;

    await fundingApplicationMutation.mutateAsync({
      id: fundingApplication.id,
      selectedConnectorSlugs: updatedSlugs,
      selectedUnsupportedAccounting: updatedUnsupportedAccounting,
      accountingDocumentsRequired: accountingConnectors.length === 1,
    });
    await queryClient.invalidateQueries({
      queryKey: submittableQueryOptions.queryKey,
    });

    setSelectedSlug(undefined);
  }, [
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    accountingConnectors.length,
    fundingApplication.id,
    fundingApplication.selectedConnectorSlugs,
    fundingApplication.selectedUnsupportedAccounting,
    fundingApplicationMutation,
    queryClient,
    selectedSlug,
    submittableQueryOptions.queryKey,
  ]);

  const handleDocumentRemoval = (documentType: FundingApplicationSchemas.DocumentType, optional: boolean) => {
    if (
      ["profit-and-loss", "balance-sheet"].includes(documentType) &&
      fundingApplicationStages.accounting?.platformRequired
    ) {
      setOpenModal(RemovalModals.ManagementAccountsRemoval);
    } else if (optional) {
      setOpenModal(RemovalModals.OptionalDocumentRemoval);
    } else {
      setOpenModal(RemovalModals.RequiredDocumentRemoval);
    }
  };

  const handleBankRemoval = () => {
    // If banking stage is included in journey, it is required and can not be removed
    setOpenModal(RemovalModals.BankConnectionRemoval);
  };

  const handlePlatformRemoval = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    (slug: string) => {
      if (ACCOUNTING_CONNECTOR_SLUGS.includes(slug)) {
        setSelectedSlug(slug);
        setOpenModal(RemovalModals.AccountingRemoval);
      } else {
        setSelectedSlug(slug);
        if (canPlatformBeRemovedNext(slug, fundingApplication, fundingApplicationStages)) {
          setOpenModal(RemovalModals.ConfirmRemovePlatform);
        } else {
          setOpenModal(RemovalModals.CannotRemovePlatform);
        }
      }
    },
    [fundingApplication, fundingApplicationStages]
  );

  const { data: uploadedDocuments } = useDocumentQuery(customerDomain, fundingApplication.id);
  const { data: uploadedValidatedDocuments } = useValidatedDocumentQuery(customerDomain, fundingApplication.id);

  const pnlValidationResults = useAccountingDocumentsValidation({
    docs: (uploadedValidatedDocuments ?? [])
      .filter((item) => item.documentType === UploadDocumentType.PROFIT_AND_LOSS)
      .flatMap(({ data }) => data as FundingApplicationSchemas.ValidatedFile[]),
  });
  const bsValidationResults = useAccountingDocumentsValidation({
    docs: (uploadedValidatedDocuments ?? [])
      .filter((item) => item.documentType === UploadDocumentType.BALANCE_SHEET)
      .flatMap(({ data }) => data as FundingApplicationSchemas.ValidatedFile[]),
  });

  const bankingItems = useBankConnectionsAsConnectionItems(
    customerDomain,
    handleBankRemoval,
    bankingSources,
    fundingApplicationStages.banking
  );

  const additionalDocumentItems = useAdditionalDocumentsAsConnectionItems(
    fundingApplication,
    fundingApplicationStages,
    handleDocumentRemoval,
    uploadedDocuments
  );

  const validatedDocumentsItems = useValidatedDocumentsAsConnectionItems({
    fundingApplication,
    fundingApplicationStages,
    validatedDocuments: uploadedValidatedDocuments,
    validationResults: {
      "profit-and-loss": pnlValidationResults,
      "balance-sheet": bsValidationResults,
    },
    onDocumentRemove: handleDocumentRemoval,
  });

  const creditCheckConsentItem = useCreditCheckConsentAsConnectionItem(customerDomain, fundingApplicationStages);

  const { completedItems: completedPlatformItems, incompleteItems: incompletePlatformItems } =
    usePlatformConnectionsAsConnectionItems(
      [...(shoppingConnectors ?? []), ...(accountingConnectors ?? []), ...(marketingConnectors ?? [])],
      fundingApplication,
      handlePlatformRemoval
    );

  const unsupportedPlatformItems = unsupportedPlatformsAsConnectionItemsProps(
    intl,
    fundingApplication,
    fundingApplicationStages,
    connectors.find((conn) => conn.slug === "ga")
  );

  const bankAndUnsupportedItems = [...bankingItems, ...unsupportedPlatformItems];

  const documentItems = [...validatedDocumentsItems, ...additionalDocumentItems];

  const completedItems = [
    ...bankAndUnsupportedItems.filter((item) => item.badgeStatus === "positive"),
    ...completedPlatformItems,
    ...documentItems.filter((item) => item.badgeStatus === "positive"),
    ...(creditCheckConsentItem && creditCheckConsentItem.badgeStatus === "positive" ? [creditCheckConsentItem] : []),
  ];
  const actionRequiredItems = [
    ...bankAndUnsupportedItems.filter((item) => item.badgeStatus !== "positive"),
    ...incompletePlatformItems,
    ...documentItems.filter((item) => item.badgeStatus !== "positive"),
    ...(creditCheckConsentItem &&
    (!creditCheckConsentItem.badgeStatus || creditCheckConsentItem.badgeStatus !== "positive")
      ? [creditCheckConsentItem]
      : []),
  ];

  const { navigate } = useSpaNavigate("FundingApp");

  const [isNotReadyModalOpen, setIsNotReadyModalOpen] = useState(false);

  return (
    <AnalyticsComponentProvider component="application-summary">
      <Flex gap="12" direction="column" maxWidth="550px">
        <ConfirmPlatformRemovalModal
          isOpen={openModal === RemovalModals.ConfirmRemovePlatform}
          closeModal={() => setOpenModal(undefined)}
          onConfirm={removeBasicPlatform}
        />
        <CannotRemovePlatformModal
          isOpen={openModal === RemovalModals.CannotRemovePlatform}
          closeModal={() => setOpenModal(undefined)}
        />
        <AccountingRemovalModal
          isOpen={openModal === RemovalModals.AccountingRemoval}
          closeModal={() => setOpenModal(undefined)}
          onConfirm={removeAccountingPlatform}
        />
        <ManagementAccountsRemovalModal
          isOpen={openModal === RemovalModals.ManagementAccountsRemoval}
          closeModal={() => setOpenModal(undefined)}
        />
        <OptionalDocumentRemovalModal
          isOpen={openModal === RemovalModals.OptionalDocumentRemoval}
          closeModal={() => setOpenModal(undefined)}
        />
        <RequiredDocumentRemovalModal
          isOpen={openModal === RemovalModals.RequiredDocumentRemoval}
          closeModal={() => setOpenModal(undefined)}
        />
        <BankConnectionRemovalModal
          isOpen={openModal === RemovalModals.BankConnectionRemoval}
          closeModal={() => setOpenModal(undefined)}
        />
        <NotReadyModal isOpen={isNotReadyModalOpen} setIsOpen={setIsNotReadyModalOpen} />

        <Flex gap="8" direction="column">
          {actionRequiredItems.length > 0 ? (
            <Flex direction="column" gap="2">
              <Heading size="lg">{intl.formatMessage(MESSAGES.actionRequired)}</Heading>
              {actionRequiredItems.map((item, idx) => (
                <ConnectionItem key={item.label + idx} {...item} />
              ))}
              {completedItems.length === 0 && !isNewOnboardingEnabled && (
                <Flex height="var(--sizes-spacing-16)">
                  <AddNewButton orientation="row-centered" onPress={() => navigate(NAVIGATION_PATHS.ADD_ANOTHER)}>
                    {intl.formatMessage(COMMON_MESSAGES.addAnother)}
                  </AddNewButton>
                </Flex>
              )}
            </Flex>
          ) : null}

          {completedItems.length > 0 ? (
            <Flex direction="column" gap="2">
              <Heading size="lg">{intl.formatMessage(MESSAGES.actionCompleted)}</Heading>
              {completedItems.map((item, idx) => {
                return <ConnectionItem key={item.label + idx} {...item} />;
              })}
              {!isNewOnboardingEnabled && (
                <Flex height="var(--sizes-spacing-16)">
                  <AddNewButton orientation="row-centered" onPress={() => navigate(NAVIGATION_PATHS.ADD_ANOTHER)}>
                    {intl.formatMessage(COMMON_MESSAGES.addAnother)}
                  </AddNewButton>
                </Flex>
              )}
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </AnalyticsComponentProvider>
  );
};
